import { App, Dropdown } from 'antd'
import { Children, FC, MouseEvent, ReactElement, cloneElement } from 'react'
import openBlobInNewTab from '../../../helpers/open-blob-in-new-tab'
import useBlankPreviewSelection from '../../../hooks/api/use-blank-preview-selection'
import useErrorModal from '../../../hooks/api/use-error-modal'
import { BLANK_TYPE_LABELS, ShipmentBlankType } from '../../../types/api/shipment-blank-type'
import { DocumentId } from '../../../types/strapi/strapi'
import UploadedFile from '../../../types/api/uploaded-file'
import {
    useLazyDownloadProductionBlankJpegQuery,
    useLazyDownloadShipmentBlankJpegQuery,
} from '../../../store/api/shipments-api'

interface PreviewShipmentBlankProps {
    children: ReactElement
    blankType: ShipmentBlankType
    shipment: { documentId: DocumentId }
    blankPreviewFiles?: UploadedFile[]
}

const PreviewShipmentBlank: FC<PreviewShipmentBlankProps> = ({
    children,
    blankType,
    shipment,
    blankPreviewFiles,
}) => {
    const { message } = App.useApp()
    const [downloadClientBlank, clientBlankState] = useLazyDownloadShipmentBlankJpegQuery()
    const [downloadProductionBlank, productionBlankState] = useLazyDownloadProductionBlankJpegQuery()

    const downloadBlank = blankType === 'client' ? downloadClientBlank : downloadProductionBlank
    const downloadBlankState = blankType === 'client' ? clientBlankState : productionBlankState

    useErrorModal(downloadBlankState, `Ошибка открытия ${BLANK_TYPE_LABELS[blankType]}`)

    const isLoading = downloadBlankState.isLoading || downloadBlankState.isFetching

    const onPreview = async (previewIndex: number) => {
        if (!shipment.documentId) return

        const res = await downloadBlank({ docId: shipment.documentId, previewIndex })
        if (!res.data) return

        const opened = openBlobInNewTab(res.data)
        if (!opened) {
            message.error('Разрешите всплывающие окна в браузере.')
        }
    }

    const { count, menuItems, runSingle } = useBlankPreviewSelection({
        blankPreviewFiles,
        onSelect: onPreview,
    })

    const child = Children.only(children)
    const disabled = !shipment.documentId || child.props.disabled || isLoading
    const loading = isLoading || child.props.loading

    if (count > 1) {
        return (
            <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={disabled}>
                {cloneElement(child, { loading, disabled })}
            </Dropdown>
        )
    }

    return cloneElement(child, {
        onClick: (event: MouseEvent) => {
            child.props.onClick?.(event)
            if (event.defaultPrevented) return
            void runSingle()
        },
        loading,
        disabled,
    })
}

export default PreviewShipmentBlank
